import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Activity, Sparkles } from "lucide-react";

/**
 * Login page.
 *
 * Demo sign-in:
 *   • The "Open demo" button calls `/api/auth/demo-login`. The backend
 *     looks up its env-configured demo user and mints a session — no
 *     credential is shipped in the frontend bundle.
 *   • If the deployment doesn't have a demo user, the button hides itself
 *     after a 404 so judges aren't shown a broken affordance.
 */
export default function Login() {
  const { user, login, demoLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoUnavailable, setDemoUnavailable] = useState(false);

  if (user && user !== false && user !== null) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (err) {
      setError(err.userMessage || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const viewDemo = async () => {
    setError(null); setDemoBusy(true);
    try {
      await demoLogin();
      nav("/", { replace: true });
    } catch (err) {
      // 404 = the deployment has no demo user; hide the button gracefully.
      if (err?.response?.status === 404) {
        setDemoUnavailable(true);
        setError("Demo home isn't enabled on this deployment.");
      } else {
        setError(err.userMessage || "Could not open the demo home.");
      }
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: Form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold">D</div>
            <div>
              <div className="font-heading font-semibold leading-tight">Dreamhouse</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Flow</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Sign in to your home.</h1>
          <p className="text-muted-foreground mt-2 text-sm">A digital twin for semi-self-sufficient households — energy, water and weather in one quiet view.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="label-tiny block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                data-testid="login-email"
                className="w-full px-3 py-2.5 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label-tiny">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="login-forgot-link"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="w-full px-3 py-2.5 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
              />
            </div>
            {error && <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5" data-testid="login-error">{error}</div>}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-2.5 rounded-md bg-foreground text-background font-medium text-sm hover:bg-zinc-200 disabled:opacity-50"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-xs text-muted-foreground text-center pt-2">
              No account? <Link to="/register" className="text-foreground underline underline-offset-2" data-testid="login-register-link">Create one</Link>
            </p>
            {!demoUnavailable && (
              <div className="border border-border rounded-md p-3 mt-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs leading-relaxed">
                    <div className="flex items-center gap-1.5 text-foreground font-medium">
                      <Sparkles className="w-3 h-3" /> View the demo home
                    </div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">
                      One-click access to the seeded showcase site.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={viewDemo}
                    disabled={demoBusy || busy}
                    data-testid="login-view-demo"
                    className="px-3 py-1.5 rounded-md text-xs font-medium border border-border hover:bg-secondary disabled:opacity-50 whitespace-nowrap"
                  >
                    {demoBusy ? "Opening…" : "Open demo →"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Right: Architectural background */}
      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/baf1959c-448b-42d0-a6ba-e254d37dd6a6/images/3a57008b0888605369a37529259b72e5b64a3911642763c0a075388fd680c4c0.png)",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="label-tiny text-white/70 mb-2 flex items-center gap-2"><Activity className="w-3 h-3" /> Live · Lierde, Belgium</div>
          <div className="font-heading text-3xl font-semibold leading-tight max-w-md">
            Energy. Water. Weather.<br/> In one quiet, honest view.
          </div>
          <div className="text-sm text-white/60 mt-2 max-w-md">Built for semi-self-sufficient homes — not just a dashboard, a digital twin.</div>
        </div>
      </div>
    </div>
  );
}
